/* QA-only collection enhancement. Native Shopify pagination remains authoritative. */
(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root && root.document && root.window === root) api.init(root);
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  const policy=typeof module==='object'&&module.exports?require('./sw-collection-order-policy-v2.js'):globalThis.SwCollectionOrderPolicy;
  const PAGE_SIZE_LIMIT = 100;
  const FEED_PAGE_SIZE = 250;
  const MAX_FEED_PAGES = 100;
  const MAX_CACHE_KEYS = 5;
  const TIMEOUT_MS = 15000;
  const PRODUCT_HANDLE = 'data-limoniapps-discountninja-product-handle';
  const EPHEMERAL_KEYS = new Set(['page', 'section_id', '_sw_stock', 'sw_loaded','sw_order_debug']);
  const QA_FLAG = 'sw_qa_load_more';
  const FIELD_QA_FLAG = 'sw_qa_field_guidance';
  const TARGET_THEME_ID = '186421346577';
  const SESSION_SNAPSHOT_KEY = 'sw-collection-load-more:v2:186421346577';
  const SESSION_SNAPSHOT_TTL_MS = 20 * 60 * 1000;

  function pageFromUrl(value, base) {
    const url = new URL(value, base || 'https://sweetweet.co.il/');
    const page = Number(url.searchParams.get('page') || 1);
    return Number.isInteger(page) && page > 0 ? page : 1;
  }

  function collectionKey(value, base) {
    const url = new URL(value, base || 'https://sweetweet.co.il/');
    for (const key of [...EPHEMERAL_KEYS, QA_FLAG]) url.searchParams.delete(key);
    url.searchParams.sort();
    return url.href;
  }

  function preservePageUrl(nextHref, currentHref) {
    const current = new URL(currentHref, 'https://sweetweet.co.il/');
    const next = new URL(nextHref, current);
    if (next.origin !== current.origin) throw new Error('cross-origin-pagination');
    const expectedPage = pageFromUrl(current) + 1;
    if (pageFromUrl(next) !== expectedPage) throw new Error('non-sequential-pagination');
    for (const [key, value] of current.searchParams) {
      if (EPHEMERAL_KEYS.has(key) || !value || next.searchParams.has(key)) continue;
      next.searchParams.append(key, value);
    }
    for (const key of ['section_id', '_sw_stock']) next.searchParams.delete(key);
    next.searchParams.set('page', String(expectedPage));
    next.searchParams.sort();
    return next.href;
  }

  function parseFeedPage(value, expectedPage) {
    const data = typeof value === 'string' ? JSON.parse(value) : value;
    if (!data || data.version !== 1 || Number(data.page) !== Number(expectedPage)) throw new Error('invalid-stock-feed-page');
    const total = Number(data.total);
    const pages = Number(data.pages);
    const offset = Number(data.offset);
    if (!Number.isInteger(total) || total < 0 || total > 25000) throw new Error('invalid-stock-feed-total');
    if (!Number.isInteger(pages) || pages < 0 || pages > MAX_FEED_PAGES) throw new Error('invalid-stock-feed-pages');
    if (!Number.isInteger(offset) || offset < 0 || !Array.isArray(data.products)) throw new Error('invalid-stock-feed-data');
    const seen = new Set();
    const products = data.products.map((pair, index) => {
      if (!Array.isArray(pair) || typeof pair[0] !== 'string' || !pair[0] || typeof pair[1] !== 'boolean' || seen.has(pair[0])) {
        throw new Error('invalid-stock-feed-product');
      }
      seen.add(pair[0]);
      return { ...policy.validateRecord(pair),handle: pair[0], available: pair[1], feels: typeof pair[2] === 'string' ? pair[2] : '', sourceIndex: offset + index };
    });
    return { page: Number(data.page), pages, total, offset, products };
  }

  function appendFeedPage(entry, data, pageSize) {
    if (entry.total !== null && entry.total !== data.total) throw new Error('collection-changed');
    if (entry.pages !== null && entry.pages !== data.pages) throw new Error('collection-pages-changed');
    if (data.page !== entry.nextPage) throw new Error('non-sequential-stock-feed');
    const handles = new Set(entry.records.map(record => record.handle));
    for (const record of data.products) {
      if (handles.has(record.handle)) throw new Error('duplicate-stock-feed-handle');
      handles.add(record.handle);
      record.sourcePage = Math.floor(record.sourceIndex / pageSize) + 1;
      delete record.sourceIndex;
      if (!Number.isInteger(record.sourcePage) || record.sourcePage < 1 || record.sourcePage > Math.max(1, Math.ceil(data.total / pageSize))) {
        throw new Error('invalid-stock-source-page');
      }
      entry.records.push(record);
    }
    entry.total = data.total;
    entry.pages = data.pages;
    entry.nextPage += 1;
    return entry;
  }

  function selectOrderedPage(records, page, pageSize) {
    const available = records.filter(record => record.available);
    const ordered = available.concat(records.filter(record => !record.available));
    const start = (Number(page) - 1) * Number(pageSize);
    return { availableCount: available.length, records: ordered.slice(start, start + Number(pageSize)), orderedCount: ordered.length };
  }

  function selectCumulativePages(records, page, pageSize) {
    const pages = [];
    for (let number = 1; number <= Number(page); number += 1) pages.push(selectOrderedPage(records, number, pageSize));
    const products = pages.flatMap(result => result.records);
    const expectedCount = Math.min(Number(page) * Number(pageSize), records.length);
    if (products.length !== expectedCount || new Set(products.map(record => record.handle)).size !== products.length) {
      throw new Error('invalid-restored-page-count');
    }
    return { pages, products, expectedCount, availableCount: pages[0] ? pages[0].availableCount : 0 };
  }

  function displayedProductCount(handles, total) {
    const count = handles instanceof Set ? handles.size : new Set(handles || []).size;
    return Math.min(count, Math.max(0, Number(total) || 0));
  }

  function mergeUniqueHandles(existing, incoming) {
    const seen = new Set(existing || []);
    const merged = [];
    for (const record of incoming || []) {
      if (!record || typeof record.handle !== 'string' || !record.handle || seen.has(record.handle)) continue;
      seen.add(record.handle);
      merged.push(record);
    }
    return merged;
  }

  function historyTransition(currentPage, targetPage, cachedPages) {
    const from = Math.max(1, Number(currentPage) || 1);
    const to = Math.max(1, Number(targetPage) || 1);
    if (to < from) return { direction: 'back', removeAfter: to, restorePages: [], requiresReload: false };
    if (to > from) {
      const cached = new Set(cachedPages || []);
      const pages = Array.from({ length: to - from }, (_, index) => from + index + 1);
      return {
        direction: 'forward',
        removeAfter: null,
        restorePages: pages.filter(page => cached.has(page)),
        requiresReload: pages.some(page => !cached.has(page))
      };
    }
    return { direction: 'same', removeAfter: null, restorePages: [], requiresReload: false };
  }

  function sessionScopeHash(value) {
    const url = new URL(value, 'https://sweetweet.co.il/');
    for (const key of [...EPHEMERAL_KEYS, QA_FLAG, FIELD_QA_FLAG, 'preview_theme_id']) url.searchParams.delete(key);
    url.searchParams.sort();
    let hash = 2166136261;
    let secondHash = 2246822519;
    for (let index = 0; index < url.href.length; index += 1) {
      const code = url.href.charCodeAt(index);
      hash ^= code;
      hash = Math.imul(hash, 16777619);
      secondHash ^= code + index;
      secondHash = Math.imul(secondHash, 3266489917);
    }
    return (hash >>> 0).toString(36) + '-' + (secondHash >>> 0).toString(36);
  }

  function createSessionSnapshot(key, page, pageSize, total, scrollY, savedAt) {
    const loadedPage = Math.max(1, Number(page) || 1);
    const size = Math.max(1, Number(pageSize) || 1);
    const count = Math.max(0, Number(total) || 0);
    return {
      version: 1,
      themeId: TARGET_THEME_ID,
      scopeHash: sessionScopeHash(key),
      page: loadedPage,
      pageSize: size,
      expectedCount: Math.min(loadedPage * size, count),
      scrollY: Math.max(0, Number(scrollY) || 0),
      savedAt: Number(savedAt) || Date.now()
    };
  }

  function isValidSessionSnapshot(snapshot, key, page, pageSize, total, now) {
    const timestamp = Number(now) || Date.now();
    if (!snapshot || snapshot.version !== 1 || snapshot.themeId !== TARGET_THEME_ID) return false;
    if (snapshot.scopeHash !== sessionScopeHash(key)) return false;
    if (Number(snapshot.page) !== Number(page) || Number(snapshot.pageSize) !== Number(pageSize) || Number(snapshot.page) <= 1) return false;
    if (Number(snapshot.expectedCount) !== Math.min(Number(page) * Number(pageSize), Number(total))) return false;
    return Number.isFinite(Number(snapshot.savedAt)) && timestamp >= Number(snapshot.savedAt)
      && timestamp - Number(snapshot.savedAt) <= SESSION_SNAPSHOT_TTL_MS;
  }

  function flagEnabled(search, themeInfo, gateEnabled) {
    const params = new URLSearchParams(search || '');
    return String(themeInfo && themeInfo.id) === TARGET_THEME_ID
      && Boolean(gateEnabled) && ['unpublished','main'].includes(themeInfo.role)
      ;
  }

  function createEntry() { return { time:Date.now(),records: [], nextPage: 1, pages: null, total: null }; }

  function init(win) {
    const doc = win.document;
    if(!doc.querySelector('[data-sw-order-release="v2"]'))return;
    const collectionPath=win.location.pathname;
    const currentSearch = () => new URL(win.location.href).search;
    if (!flagEnabled(currentSearch(), win.Shopify && win.Shopify.theme, !!doc.querySelector('[data-sw-order-release="v2"]'))) return;
    const cachedEntries = new Map();
    let root = null;
    let grid = null;
    let toolbar = null;
    let button = null;
    let live = null;
    let currentPage = 1;
    let pageSize = 0;
    let currentTotal = null;
    let currentKey = '';
    const loadedPageNodes = new Map();
    const pageScrollPositions = new Map();
    let scrollSaveQueued = false;
    let restoring = false;
    let activeController = null;
    let loading = false;
    let pendingScrollRestore = null;

    function getRoot() {
      const found = doc.querySelector('[data-sw-stock]');
      if (!found) return null;
      const foundGrid = found.querySelector('.collection-products-grid');
      if (!foundGrid) return null;
      root = found;
      grid = foundGrid;
      pageSize = Math.max(1, Number(root.dataset.swSize) || 0);
      currentPage = pageFromUrl(win.location.href);
      currentKey = collectionKey(win.location.href);
      if (!pageScrollPositions.has(currentPage)) pageScrollPositions.set(currentPage, Number(win.scrollY) || 0);
      return root;
    }

    function getEntry(key) {
      const now = Date.now();
      let existing = cachedEntries.get(key);
      if (existing && (now < existing.time || now - existing.time > 30000)) {
        cachedEntries.delete(key);
        existing = null;
      }
      // Initial ordering has already paid for this scoped feed. Reuse its safe
      // snapshot for both Load More and cumulative restoration, without sharing
      // mutable record objects or extending the original inventory timestamp.
      const scope = policy.feedScope(key, root.dataset.swCollectionId, pageSize);
      const seeded = policy.seedFeed(win.swStockOrder && win.swStockOrder.feedSnapshot, scope, now);
      if (seeded && (!existing || seeded.time > existing.time
          || (seeded.time === existing.time && seeded.records.length > existing.records.length))) {
        existing = { time: seeded.time, records: seeded.records, nextPage: seeded.next, pages: seeded.pages, total: seeded.total };
      }
      const entry = existing || createEntry();
      cachedEntries.delete(key);
      cachedEntries.set(key, entry);
      while (cachedEntries.size > MAX_CACHE_KEYS) cachedEntries.delete(cachedEntries.keys().next().value);
      return entry;
    }

    async function fetchText(url, signal) {
      const controller = new win.AbortController();
      let timedOut = false;
      const timeout = win.setTimeout(() => { timedOut = true; controller.abort(); }, TIMEOUT_MS);
      const cancel = () => controller.abort();
      signal && signal.addEventListener('abort', cancel, { once: true });
      try {
        const response = await win.fetch(url, { credentials: 'same-origin', signal: controller.signal });
        if (!response.ok) throw new Error(`http-${response.status}`);
        return await response.text();
      } catch (error) {
        if (timedOut) throw new Error('timeout');
        if (signal && signal.aborted) throw new Error('cancelled');
        throw error;
      } finally {
        win.clearTimeout(timeout);
        signal && signal.removeEventListener('abort', cancel);
      }
    }

    function feelRecords(entry){const feel=win.swSquishyCategory?.choice(root,new URL(win.location.href));const records=feel?entry.records.filter(record=>(record.feels||'').split(',').includes(feel)):entry.records;return policy.ordered(records,policy.context(root.dataset.swCollectionId,win.location.href),entry.total!==null&&entry.nextPage>entry.pages);}

    async function ensureFeedThrough(entry, key, page, size, signal) {
      const start = (page - 1) * size;
      while (entry.pages === null || policy.needsMore(entry.records,start+size,policy.context(root.dataset.swCollectionId,key),entry.total!==null&&entry.nextPage>entry.pages,win.swSquishyCategory?.choice(root,new URL(key)))) {
        if (entry.nextPage > MAX_FEED_PAGES || (entry.pages !== null && entry.nextPage > entry.pages)) break;
        const first = entry.nextPage;
        const last = entry.pages === null ? first : Math.min(first + 2, entry.pages);
        const requests = [];
        for (let feedPage = first; feedPage <= last; feedPage += 1) {
          const url = policy.sourceUrl(key,root.dataset.swCollectionId);
          url.searchParams.set('view', 'sw-order-data-v2');
          url.searchParams.set('page', String(feedPage));
          requests.push(fetchText(url.href, signal).then(body => parseFeedPage(body, feedPage)));
        }
        const results = await Promise.all(requests);
        for (const result of results) appendFeedPage(entry, result, size);
        if (entry.total === 0 || entry.nextPage > entry.pages) break;
      }
      if(entry.nextPage>entry.pages&&entry.records.length!==entry.total)throw Error('incomplete-policy-feed');
      return entry;
    }

    function addCardsFromHtml(html, map) {
      const parsed = new win.DOMParser().parseFromString(html, 'text/html');
      const parsedGrid = parsed.querySelector('.collection-products-grid');
      if (!parsedGrid) throw new Error('missing-product-grid');
      parsedGrid.querySelectorAll(`:scope > [${PRODUCT_HANDLE}]`).forEach(card => {
        const handle = card.getAttribute(PRODUCT_HANDLE);
        if (handle && !map.has(handle)) map.set(handle, card);
      });
    }

    async function fetchCards(records, key, signal) {
      const cards = new Map();
      if (grid) grid.querySelectorAll(`:scope > [${PRODUCT_HANDLE}]`).forEach(card => cards.set(card.getAttribute(PRODUCT_HANDLE), card));
      const missing = records.filter(record => !cards.has(record.handle));
      const pages = [...new Set(missing.map(record => record.sourcePage))];
      for (let index = 0; index < pages.length; index += 3) {
        const batch = pages.slice(index, index + 3);
        await Promise.all(batch.map(async sourcePage => {
          const url = policy.sourceUrl(key,root.dataset.swCollectionId);
          url.searchParams.set('page', String(sourcePage));
          url.searchParams.set('section_id', root.dataset.swSection || '');
          const html = await fetchText(url.href, signal);
          if (!root.isConnected || collectionKey(win.location.href) !== currentKey) throw new Error('stale-load');
          addCardsFromHtml(html, cards);
        }));
      }
      return cards;
    }

    function currentProductHandles() {
      return new Set([...grid.querySelectorAll(`:scope > [${PRODUCT_HANDLE}]`)].map(card => card.getAttribute(PRODUCT_HANDLE)));
    }

    function clearSessionSnapshot() {
      try { win.sessionStorage.removeItem(SESSION_SNAPSHOT_KEY); } catch (_) { /* Storage may be unavailable in private contexts. */ }
    }

    function saveSessionSnapshot(page) {
      if (Number(page) <= 1 || currentTotal === null) {
        clearSessionSnapshot();
        return;
      }
      const snapshot = createSessionSnapshot(currentKey, page, pageSize, currentTotal, Number(win.scrollY) || 0);
      try { win.sessionStorage.setItem(SESSION_SNAPSHOT_KEY, JSON.stringify(snapshot)); } catch (_) { /* Storage may be unavailable in private contexts. */ }
    }

    function readSessionSnapshot() {
      try {
        const raw = win.sessionStorage.getItem(SESSION_SNAPSHOT_KEY);
        return raw ? JSON.parse(raw) : null;
      } catch (_) { return null; }
    }

    async function restoreAccumulatedPages(snapshot) {
      if (restoring || loading || !root || !grid) return;
      const restoreKey = currentKey;
      const expectedCount = Math.min(snapshot.page * pageSize, currentTotal);
      if (currentProductHandles().size >= expectedCount) {
        win.requestAnimationFrame(() => win.scrollTo(0, snapshot.scrollY));
        return;
      }
      restoring = true;
      root.dataset.swRestoringMore = 'true';
      const restoreController = new win.AbortController();
      activeController = restoreController;
      const signal = restoreController.signal;
      let restorationError = false;
      setStatus(isHebrew() ? 'משחזרים את המוצרים שהוצגו…' : 'Restoring previously shown products…', false);
      syncToolbar();
      try {
        const entry = getEntry(currentKey);
        await ensureFeedThrough(entry, currentKey, snapshot.page, pageSize, signal);
        const restored = selectCumulativePages(feelRecords(entry), snapshot.page, pageSize);
        const pageRecords = restored.pages;
        const records = restored.products;
        if (records.length !== expectedCount) throw new Error('invalid-restored-page-count');
        const cards = await fetchCards(records, currentKey, signal);
        if (signal.aborted || !root.isConnected || collectionKey(win.location.href) !== currentKey) throw new Error('cancelled');
        const fragment = doc.createDocumentFragment();
        const pageNodes = new Map();
        let globalIndex = 0;
        const availableCount = restored.availableCount;
        for (let page = 1; page <= snapshot.page; page += 1) {
          const nodes = [];
          for (const record of pageRecords[page - 1].records) {
            if (!record.available && globalIndex === availableCount && !pageNodes.has('divider')) {
              const divider = doc.createElement('div');
              divider.className = 'sw-stock-divider';
              divider.dataset.swLoadPage = String(page);
              const heading = doc.createElement('h2');
              heading.textContent = isHebrew() ? 'אזלו מהמלאי' : 'Out of stock';
              divider.append(heading);
              fragment.append(divider);
              nodes.push(divider);
              pageNodes.set('divider', true);
            }
            const card = cards.get(record.handle);
            if (!card) throw new Error('missing-restored-product-card');
            const availability = card.getAttribute('data-limoniapps-discountninja-product-available') === 'true';
            if (availability !== record.available) throw new Error('availability-changed');
            const clone = card.cloneNode(true);
            clone.dataset.swLoadPage = String(page);
            fragment.append(clone);
            nodes.push(clone);
            globalIndex += 1;
          }
          pageNodes.set(page, nodes);
        }
        grid.replaceChildren(fragment);
        loadedPageNodes.clear();
        for (let page = 1; page <= snapshot.page; page += 1) loadedPageNodes.set(page, pageNodes.get(page) || []);
        for (const nodes of loadedPageNodes.values()) {
          nodes.forEach(card => {
            if (!card.matches || !card.matches(`[${PRODUCT_HANDLE}]`)) return;
            const forms = card.querySelectorAll('form.ajax-product-form');
            if (win.theme && win.theme.initAjaxAddToCartForm && win.jQuery) {
              forms.forEach(form => win.theme.initAjaxAddToCartForm(win.jQuery(form)));
            }
          });
        }
        if (win.lazySizes && win.lazySizes.loader && win.lazySizes.loader.checkElems) win.lazySizes.loader.checkElems();
        currentPage = snapshot.page;
        root.dataset.swPage = String(currentPage);
        pageScrollPositions.set(currentPage, snapshot.scrollY);
        updateCanonical(new URL(win.location.href));
        saveSessionSnapshot(currentPage);
        win.swCollectionOrderHistory={phase:'restored',page:currentPage,visible:currentProductHandles().size,snapshotKey:SESSION_SNAPSHOT_KEY};
        doc.dispatchEvent(new win.CustomEvent('sw-order-history-ready'));
      } catch (error) {
        if (!error || error.message !== 'cancelled') {
          restorationError = true;
        }
      } finally {
        restoring = false;
        if (activeController === restoreController) activeController = null;
        if (root && collectionKey(win.location.href) === restoreKey) {
          delete root.dataset.swRestoringMore;
          syncToolbar();
          if (restorationError) {
            const shown = displayedProductCount(currentProductHandles(), currentTotal);
            setStatus(isHebrew()
              ? `השחזור לא הושלם · מוצגים ${shown} מתוך ${currentTotal}. אפשר לבחור עמוד.`
              : `Could not restore · showing ${shown} of ${currentTotal}. Use the page links.`, true);
          }
          win.requestAnimationFrame(() => win.scrollTo(0, snapshot.scrollY));
        }
      }
    }

    function setStatus(message, error) {
      if (!live) return;
      live.textContent = message;
      live.classList.toggle('sw-load-more__status--error', Boolean(error));
    }

    function updateCanonical(url) {
      const canonical = doc.querySelector('link[rel="canonical"]');
      if (!canonical) return;
      const value = new URL(url.href);
      value.searchParams.delete(QA_FLAG);
      value.searchParams.delete('sw_qa_field_guidance');
      value.searchParams.delete('preview_theme_id');
      value.searchParams.delete('sw_loaded');
      value.searchParams.delete('section_id');
      value.searchParams.delete('view');
      value.searchParams.delete('_sw_stock');
      canonical.href = value.href;
    }

    function makePageUrl(page, baseUrl) {
      const url = new URL(baseUrl || win.location.href);
      url.searchParams.set('page', String(page));
      url.searchParams.delete('sw_loaded');
      url.searchParams.delete('section_id');
      
      url.searchParams.delete('_sw_stock');
      url.searchParams.sort();
      return url;
    }

    function renderPagination(nav, page, pages, baseUrl) {
      if (!nav) return;
      nav.replaceChildren();
      nav.classList.add('pagination', 'large-row', 'sw-stock-paging');
      nav.setAttribute('aria-label', isHebrew() ? 'עמודי מוצרים' : 'Product pages');
      const addPage = (number, label, current) => {
        const span = doc.createElement('span');
        span.className = current ? 'page current' : 'page';
        if (current) {
          span.textContent = label;
          span.setAttribute('aria-current', 'page');
        } else {
          const anchor = doc.createElement('a');
          anchor.href = makePageUrl(number, baseUrl).href;
          anchor.textContent = label;
          span.append(anchor);
        }
        nav.append(span);
      };
      if (page > 1) addPage(page - 1, isHebrew() ? '« הקודם' : '« Previous', false);
      const visible = [...new Set([1, page - 1, page, page + 1, pages])].filter(number => number >= 1 && number <= pages).sort((a,b) => a-b);
      let prior = 0;
      visible.forEach(number => {
        if (prior && number - prior > 1) nav.append(doc.createTextNode(' … '));
        addPage(number, String(number), number === page);
        prior = number;
      });
      if (page < pages) addPage(page + 1, isHebrew() ? 'הבא »' : 'Next »', false);
    }

    function isHebrew() { return /^he(?:-|$)/i.test(doc.documentElement.lang || ''); }

    function ensureToolbar(nav) {
      if (!toolbar) {
        toolbar = doc.createElement('div');
        toolbar.className = 'sw-load-more-controls';
        toolbar.setAttribute('role', 'group');
        toolbar.setAttribute('aria-label', isHebrew() ? 'טעינת מוצרים נוספת' : 'Load more products');
        button = doc.createElement('button');
        button.className = 'btn sw-load-more__button';
        button.type = 'button';
        button.textContent = isHebrew() ? 'טען עוד' : 'Load more';
        live = doc.createElement('p');
        live.className = 'sw-load-more__status';
        live.setAttribute('role', 'status');
        live.setAttribute('aria-live', 'polite');
        live.setAttribute('aria-atomic', 'true');
        button.addEventListener('click', loadNext);
        toolbar.append(button, live);
      }
      if (nav) {
        let row = nav.parentElement;
        if (!row.classList.contains('sw-load-more-row')) {
          row = doc.createElement('div');
          row.className = 'sw-load-more-row';
          nav.before(row);
          row.append(nav);
        }
        if (toolbar.parentElement !== row) row.append(toolbar);
      }
    }

    function findNextPageHref(nav, page) {
      if (!nav) return null;
      for (const anchor of nav.querySelectorAll('a[href]')) {
        try {
          const url = new URL(anchor.href, win.location.href);
          if (pageFromUrl(url) === page + 1) return url.href;
        } catch (_) { /* Ignore malformed theme links; other real page links remain available. */ }
      }
      return null;
    }

    function syncToolbar(metrics, preserveStatus) {
      if (!getRoot() || root.dataset.swState !== 'ready') return;
      const nav = root.querySelector('.sw-stock-paging, .pagination');
      if (!nav) return;
      currentPage = pageFromUrl(win.location.href);
      pageSize = Math.max(1, Number(root.dataset.swSize) || pageSize);
      if (metrics && Number.isFinite(Number(metrics.total))) currentTotal = Number(metrics.total);
      const pages = currentTotal === null ? null : Math.ceil(currentTotal / pageSize);
      if (pages === null || pages <= 1) return;
      renderPagination(nav, currentPage, pages, win.location.href);
      ensureToolbar(nav);
      const next = currentPage < pages ? findNextPageHref(nav, currentPage) : null;
      const shown = displayedProductCount(currentProductHandles(), currentTotal);
      button.hidden = !restoring && !next;
      button.disabled = loading || restoring;
      button.textContent = isHebrew() ? 'טען עוד' : 'Load more';
      if (restoring) {
        setStatus(isHebrew() ? 'משחזרים את המוצרים שהוצגו…' : 'Restoring previously shown products…', false);
        return;
      }
      if (preserveStatus) return;
      setStatus(isHebrew() ? `הוצגו ${shown} מתוך ${currentTotal} מוצרים` : `Showing ${shown} of ${currentTotal} products`, false);
    }

    async function loadNext() {
      if (loading || restoring || !getRoot() || root.dataset.swState !== 'ready') return;
      const nav = root.querySelector('.sw-stock-paging, .pagination');
      const nextHref = findNextPageHref(nav, currentPage);
      if (!nextHref) return;
      let targetUrl;
      try { targetUrl = new URL(preservePageUrl(nextHref, win.location.href)); }
      catch (_) { setStatus(isHebrew() ? 'לא ניתן לפתוח את העמוד הבא. קישורי העימוד עדיין זמינים.' : 'The next page link could not be used. Page links are still available.', true); return; }
      const targetPage = currentPage + 1;
      loading = true;
      activeController = new win.AbortController();
      const signal = activeController.signal;
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      grid.setAttribute('aria-busy', 'true');
      root.dataset.swLoadingMore = 'true';
      setStatus(isHebrew() ? `טוענים עמוד ${targetPage}…` : `Loading page ${targetPage}…`, false);
      const startedAt = win.performance && win.performance.now ? win.performance.now() : 0;
      try {
        const entry = getEntry(currentKey);
        await ensureFeedThrough(entry, currentKey, targetPage, pageSize, signal);
        const totalPages = Math.ceil((win.swSquishyCategory?.choice(root,new URL(currentKey)) ? feelRecords(entry).length : entry.total) / pageSize);
        if (targetPage > totalPages) throw new Error('no-more-pages');
        const policyRows=feelRecords(entry);
        const actualPrefix=[...currentProductHandles()];
        const expectedPrefix=policyRows.slice(0,currentPage*pageSize).map(r=>r.handle);
        if(actualPrefix.length===currentPage*pageSize && JSON.stringify(actualPrefix)!==JSON.stringify(expectedPrefix)){win.location.replace(win.location.href);throw Error('cancelled');}
        const pageData = selectOrderedPage(policyRows, targetPage, pageSize);
        if (!pageData.records.length) throw new Error('empty-next-page');
        const cardMap = await fetchCards(pageData.records, currentKey, signal);
        if (signal.aborted || !root.isConnected || collectionKey(win.location.href) !== currentKey) throw new Error('cancelled');
        const existing = currentProductHandles();
        const appendRecords = mergeUniqueHandles(existing, pageData.records);
        if (appendRecords.length !== pageData.records.filter(record => !existing.has(record.handle)).length) throw new Error('duplicate-product-state');
        const fragment = doc.createDocumentFragment();
        const appended = [];
        for (const record of appendRecords) {
          const card = cardMap.get(record.handle);
          if (!card) throw new Error('missing-product-card');
          const availability = card.getAttribute('data-limoniapps-discountninja-product-available') === 'true';
          if (availability !== record.available) throw new Error('availability-changed');
          const logicalIndex = (targetPage - 1) * pageSize + pageData.records.findIndex(item => item.handle === record.handle);
          if (!record.available && logicalIndex === pageData.availableCount && !grid.querySelector('.sw-stock-divider')) {
            const divider = doc.createElement('div');
            divider.className = 'sw-stock-divider';
            divider.dataset.swLoadPage = String(targetPage);
            const heading = doc.createElement('h2');
            heading.textContent = isHebrew() ? 'אזלו מהמלאי' : 'Out of stock';
            divider.append(heading);
            fragment.append(divider);
            appended.push(divider);
          }
          const clone = card.cloneNode(true);
          clone.dataset.swLoadPage = String(targetPage);
          fragment.append(clone);
          appended.push(clone);
        }
        if (!appended.length) throw new Error('duplicate-page');
        grid.append(fragment);
        // Animate only this new batch, never replay on restored history pages.
        if (!win.matchMedia || !win.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          appended.forEach(card => {
            if (typeof card.animate === 'function') card.animate(
              [{ opacity: 0, transform: 'translateY(8px)' }, { opacity: 1, transform: 'translateY(0)' }],
              { duration: 220, easing: 'ease-out' }
            );
          });
        }
        loadedPageNodes.set(targetPage, appended);
        appended.forEach(card => {
          if (card.matches && card.matches(`[${PRODUCT_HANDLE}]`)) {
            const forms = card.querySelectorAll('form.ajax-product-form');
            if (win.theme && win.theme.initAjaxAddToCartForm && win.jQuery) {
              forms.forEach(form => win.theme.initAjaxAddToCartForm(win.jQuery(form)));
            }
          }
        });
        if (win.lazySizes && win.lazySizes.loader && win.lazySizes.loader.checkElems) win.lazySizes.loader.checkElems();
        const priorUrl = new URL(win.location.href);
        const priorScrollY = Number(win.scrollY) || 0;
        pageScrollPositions.set(currentPage, priorScrollY);
        const priorState = { ...(win.history.state || {}), searchParams: priorUrl.searchParams.toString(), swLoadMore: { page: currentPage, scrollY: priorScrollY } };
        win.history.replaceState(priorState, '', priorUrl.href);
        targetUrl.searchParams.set('sw_loaded',String(targetPage));
        const nextState = { ...(win.history.state || {}), searchParams: targetUrl.searchParams.toString(), swLoadMore: { page: targetPage, scrollY: priorScrollY } };
        win.history.pushState(nextState, '', targetUrl.href);
        win.swCollectionOrderHistory={phase:'committed',from:priorUrl.href,to:targetUrl.href,snapshotKey:SESSION_SNAPSHOT_KEY,page:targetPage};
        currentPage = targetPage;
        pageScrollPositions.set(targetPage, priorScrollY);
        currentTotal = win.swSquishyCategory?.choice(root,new URL(currentKey)) ? feelRecords(entry).length : entry.total;
        saveSessionSnapshot(targetPage);
        root.dataset.swPage = String(currentPage);
        updateCanonical(targetUrl);
        renderPagination(nav, currentPage, totalPages, targetUrl.href);
        const count = appendRecords.length;
        const visibleCount = displayedProductCount(currentProductHandles(), currentTotal);
        const duration = startedAt && win.performance ? Math.max(0, Math.round(win.performance.now() - startedAt)) : 0;
        setStatus(isHebrew() ? `נוספו ${count} מוצרים · הוצגו ${visibleCount} מתוך ${currentTotal}${duration > 5000 ? ' · הטעינה ארכה מעט' : ''}` : `Added ${count} products · showing ${visibleCount} of ${currentTotal}${duration > 5000 ? ' · This load took longer' : ''}`, false);
      } catch (error) {
        if (error && error.message === 'cancelled') return;
        activeController && activeController.abort();
        const message = error && error.message === 'timeout'
          ? (isHebrew() ? 'הטעינה התעכבה. אפשר לנסות שוב או לבחור עמוד.' : 'The request timed out. Retry or choose a page.')
          : (isHebrew() ? 'לא ניתן לטעון כרגע. אפשר לנסות שוב או לבחור עמוד.' : 'Could not load more products. Retry or choose a page.');
        setStatus(message, true);
      } finally {
        loading = false;
        activeController = null;
        if (button) { button.disabled = false; button.removeAttribute('aria-busy'); }
        if (grid) grid.removeAttribute('aria-busy');
        if (root) delete root.dataset.swLoadingMore;
        syncToolbar(null, true);
      }
    }

    function onFiltersApplied() {
      activeController && activeController.abort();
      activeController = null;
      loading = false;
      restoring = false;
      if (root) delete root.dataset.swRestoringMore;
      clearSessionSnapshot();
      toolbar = button = live = null;
      cachedEntries.clear();
      loadedPageNodes.clear();
      pageScrollPositions.clear();
      getRoot();
      const url = new URL(win.location.href);
      currentPage = pageFromUrl(url);
      currentKey = collectionKey(url);
      pageScrollPositions.set(currentPage, Number(win.scrollY) || 0);
      currentTotal = null;
      updateCanonical(url);
    }

    function onStockReady(event) {
      if (!getRoot()) return;
      currentPage = pageFromUrl(win.location.href);
      currentKey = collectionKey(win.location.href);
      currentTotal = event && event.detail ? Number(event.detail.total) : currentTotal;
      syncToolbar(event && event.detail);
      const markedPage=Number(new URL(win.location.href).searchParams.get('sw_loaded'));
      const urlRestore=markedPage===currentPage&&currentPage>1;
      const snapshot = urlRestore?createSessionSnapshot(currentKey,currentPage,pageSize,currentTotal,Number(win.history.state?.swLoadMore?.scrollY)||0):readSessionSnapshot();
      if(urlRestore)win.swCollectionOrderHistory={phase:'restore-from-url',page:currentPage,expectedCount:snapshot.expectedCount,snapshotKey:SESSION_SNAPSHOT_KEY};
      if (snapshot && isValidSessionSnapshot(snapshot, currentKey, currentPage, pageSize, currentTotal)) {
        void restoreAccumulatedPages(snapshot);
      } else if (snapshot) {
        clearSessionSnapshot();
      }
      if (pendingScrollRestore !== null) {
        const y = pendingScrollRestore;
        pendingScrollRestore = null;
        win.requestAnimationFrame(() => win.scrollTo(0, y));
      }
    }

    function onPopState(event) {
      if(!doc.querySelector('[data-sw-order-release="v2"]')||new URL(win.location.href).pathname!==collectionPath)return;
      event.stopImmediatePropagation();
      activeController && activeController.abort();
      const target=new URL(win.location.href),targetPage=pageFromUrl(target),sameScope=collectionKey(target)===currentKey;
      win.swCollectionOrderHistory={phase:'history-reload',to:target.href,page:targetPage,snapshotKey:SESSION_SNAPSHOT_KEY};
      const state=event.state&&event.state.swLoadMore;
      if(sameScope&&targetPage>1&&state&&Number(state.page)===targetPage&&currentTotal!==null){
        const snapshot=createSessionSnapshot(target.href,targetPage,pageSize,currentTotal,Number(state.scrollY)||0);
        try{win.sessionStorage.setItem(SESSION_SNAPSHOT_KEY,JSON.stringify(snapshot));}catch(_){}
      }else clearSessionSnapshot();
      win.location.replace(target.href);
    }

    doc.addEventListener('filters-applied', onFiltersApplied, true);
    doc.addEventListener('sw-stock-ready', onStockReady);
    win.addEventListener('popstate', onPopState, true);
    win.addEventListener('scroll', () => {
      if (scrollSaveQueued || !(win.history.state && win.history.state.swLoadMore)) return;
      scrollSaveQueued = true;
      win.requestAnimationFrame(() => {
        scrollSaveQueued = false;
        const state = win.history.state || {};
        if (!state.swLoadMore) return;
        const scrollY = Number(win.scrollY) || 0;
        pageScrollPositions.set(currentPage, scrollY);
        win.history.replaceState({ ...state, swLoadMore: { ...state.swLoadMore, page: currentPage, scrollY } }, '', win.location.href);
        saveSessionSnapshot(currentPage);
      });
    }, { passive: true });
    win.addEventListener('pageshow', event => { if (event.persisted) onStockReady({ detail: win.swStockOrder && win.swStockOrder.metrics }); });

    if (getRoot() && root.dataset.swState === 'ready' && win.swStockOrder && win.swStockOrder.status === 'ready') {
      onStockReady({ detail: win.swStockOrder.metrics });
    }
  }

  return {
    pageFromUrl,
    collectionKey,
    preservePageUrl,
    parseFeedPage,
    appendFeedPage,
    selectOrderedPage,
    selectCumulativePages,
    displayedProductCount,
    mergeUniqueHandles,
    historyTransition,
    sessionScopeHash,
    createSessionSnapshot,
    isValidSessionSnapshot,
    flagEnabled,
    init
  };
});


